// ตารางราคา 2 แบบ (ใช้ทั้งหน้า Landing และ /pricing) — แบบสมาชิกรายเดือน/รายปี และจ่ายตามการใช้งาน token
import React, { useMemo, useState } from "react";
import { baht, fmtTokens, fmtNum, perMillion, yearlySaving, customMonthly, customPrice, paygTokens, CUSTOM_STEPS_M, CUSTOM_PLAN_ID } from "../../services/pricing";
import { Badge, Button, Icon, Segmented } from "../../components/ui";

export function PlanCards({ pricing, current, onChoose, cycle, setCycle }) {
  const maxSave = Math.max(0, ...pricing.plans.map(yearlySaving));
  return (
    <>
      <div className="center mb-24">
        <Segmented
          value={cycle}
          onChange={setCycle}
          options={[
            { value: "monthly", label: "รายเดือน" },
            { value: "yearly", label: "รายปี", badge: maxSave ? `ประหยัด ${maxSave}%` : undefined },
          ]}
        />
      </div>
      <div className="plan-grid">
        {pricing.plans.map((p) => {
          const price = cycle === "yearly" ? p.yearly : p.monthly;
          const perMonth = cycle === "yearly" && p.yearly ? Math.round(p.yearly / 12) : p.monthly;
          const isCur = current?.planId === p.id;
          return (
            <div key={p.id} className={"plan" + (p.popular ? " is-popular" : "") + (isCur ? " is-current" : "")}>
              {p.popular ? <div className="plan-ribbon">ยอดนิยม</div> : null}
              <div className="plan-name">
                {p.id !== "free" ? <Icon name={p.id === "max" ? "crown" : "zap"} size={18} /> : null}
                {p.name}
              </div>
              <div className="plan-tag">{p.tagline}</div>
              <div className="plan-price">
                <b>{price ? baht(perMonth) : "ฟรี"}</b>
                {price ? <span>/ เดือน</span> : null}
              </div>
              <div className="plan-bill">
                {price ? (cycle === "yearly" ? <>เรียกเก็บ {baht(p.yearly)} ต่อปี {yearlySaving(p) ? <Badge tone="success">ประหยัด {yearlySaving(p)}%</Badge> : null}</> : "เรียกเก็บรายเดือน ยกเลิกได้ทุกเมื่อ") : "ไม่ต้องใช้บัตร"}
              </div>
              <div className="plan-quota">
                <Icon name="zap" size={15} />
                <b>{fmtTokens(p.tokens)}</b> token / เดือน
              </div>
              {p.id === "free" ? (
                <Button block variant="default" disabled>
                  {isCur ? "แพ็กเกจปัจจุบัน" : "ใช้ได้ทันทีเมื่อสมัคร"}
                </Button>
              ) : (
                <Button block variant={p.popular ? "primary" : "dark"} onClick={() => onChoose({ kind: "subscription", plan: p, cycle, price })}>
                  {isCur ? "ต่ออายุแพ็กเกจ" : `เลือก ${p.name}`}
                </Button>
              )}
              <ul className="plan-features">
                {(p.features || []).map((f) => (
                  <li key={f}>
                    <Icon name="check" size={15} stroke={2.4} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        <CustomPlanCard pricing={pricing} current={current} cycle={cycle} onChoose={onChoose} />
      </div>
    </>
  );
}

/** แพ็กเกจกำหนดเอง — เลื่อนเลือกโควตา token/เดือน ราคาคำนวณสด */
function CustomPlanCard({ pricing, current, cycle, onChoose }) {
  const c = pricing.custom;
  const steps = useMemo(() => CUSTOM_STEPS_M.map((m) => m * 1e6).filter((t) => t >= c.minTokens && t <= c.maxTokens), [c.minTokens, c.maxTokens]);
  const [idx, setIdx] = useState(Math.min(3, steps.length - 1));
  const tokens = steps[idx] || c.minTokens;
  const mo = customMonthly(pricing, tokens);
  const price = customPrice(pricing, tokens, cycle);
  const perMonth = cycle === "yearly" ? Math.round(price / 12) : mo;
  const save = cycle === "yearly" ? Math.round((1 - price / (mo * 12)) * 100) : 0;
  const isCur = current?.planId === CUSTOM_PLAN_ID;
  return (
    <div className={"plan plan-custom" + (isCur ? " is-current" : "")}>
      <div className="plan-name"><Icon name="layers" size={18} />Custom</div>
      <div className="plan-tag">ปรับโควตาได้ตามใจ ยิ่งเยอะยิ่งถูก</div>
      <div className="plan-price">
        <b>{baht(perMonth)}</b>
        <span>/ เดือน</span>
      </div>
      <div className="plan-bill">
        {cycle === "yearly" ? <>เรียกเก็บ {baht(price)} ต่อปี {save ? <Badge tone="success">ประหยัด {save}%</Badge> : null}</> : `≈ ${baht(Math.round(mo / (tokens / 1e6)))} ต่อ 1M token`}
      </div>
      <div className="slider-box">
        <div className="slider-val">
          <Icon name="zap" size={15} />
          <b>{fmtTokens(tokens)}</b> token / เดือน
        </div>
        <input type="range" className="slider" min={0} max={steps.length - 1} step={1} value={idx} onChange={(e) => setIdx(Number(e.target.value))} aria-label="โควตา token ต่อเดือน" style={{ "--p": `${(idx / Math.max(1, steps.length - 1)) * 100}%` }} />
        <div className="slider-ends"><span>{fmtTokens(steps[0])}</span><span>{fmtTokens(steps[steps.length - 1])}</span></div>
      </div>
      <Button block variant="dark" onClick={() => onChoose({ kind: "custom", tokens, cycle, price })}>
        เลือก Custom {fmtTokens(tokens)}
      </Button>
      <ul className="plan-features">
        <li><Icon name="check" size={15} stroke={2.4} />เลือกโควตาเองตั้งแต่ {fmtTokens(steps[0])} – {fmtTokens(steps[steps.length - 1])} / เดือน</li>
        <li><Icon name="check" size={15} stroke={2.4} />ได้ทุกอย่างของ Pro</li>
        <li><Icon name="check" size={15} stroke={2.4} />เปลี่ยนขนาดได้ทุกเมื่อ</li>
      </ul>
    </div>
  );
}

/** จ่ายตามการใช้งาน — เลื่อน/พิมพ์ยอดเงินเอง หรือกดแพ็คสำเร็จรูป */
export function PackCards({ pricing, onChoose }) {
  const g = pricing.payg;
  const [amount, setAmount] = useState(() => Number(pricing.packs.find((k) => k.popular)?.price) || g.minBaht);
  const clamp = (v) => Math.min(g.maxBaht, Math.max(g.minBaht, Math.round((Number(v) || 0) / g.step) * g.step));
  const tokens = paygTokens(pricing, amount);
  const base = paygTokens(pricing, g.minBaht) / g.minBaht;
  const bonus = base ? Math.round((tokens / amount / base - 1) * 100) : 0;
  const pct = ((amount - g.minBaht) / Math.max(1, g.maxBaht - g.minBaht)) * 100;
  return (
    <div className="payg">
      <div className="payg-main">
        <div className="payg-label">เลือกยอดที่ต้องการเติม</div>
        <div className="payg-amount">
          <span>฿</span>
          <input
            type="number"
            inputMode="numeric"
            min={g.minBaht}
            max={g.maxBaht}
            step={g.step}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            onBlur={() => setAmount(clamp(amount))}
            aria-label="จำนวนเงิน (บาท)"
          />
        </div>
        <input type="range" className="slider" min={g.minBaht} max={g.maxBaht} step={g.step} value={clamp(amount)} onChange={(e) => setAmount(Number(e.target.value))} aria-label="เลื่อนเลือกยอดเงิน" style={{ "--p": `${Math.max(0, Math.min(100, pct))}%` }} />
        <div className="slider-ends"><span>{baht(g.minBaht)}</span><span>{baht(g.maxBaht)}</span></div>
        <div className="payg-presets">
          {pricing.packs.map((k) => (
            <button key={k.id} className={"chip" + (Number(amount) === Number(k.price) ? " is-active" : "")} onClick={() => setAmount(k.price)}>
              {baht(k.price)}
              {k.popular ? <span className="chip-hot">คุ้ม</span> : null}
            </button>
          ))}
        </div>
      </div>
      <div className="payg-result">
        <div className="faint small">คุณจะได้รับ</div>
        <div className="payg-tokens"><Icon name="zap" size={26} />{fmtNum(tokens)}</div>
        <div className="small muted">token · ไม่มีวันหมดอายุ</div>
        <div className="payg-meta">
          <span>≈ {baht(Math.round(perMillion({ tokens, price: amount }) || 0))} / 1M token</span>
          {bonus > 0 ? <Badge tone="success">โบนัส +{bonus}%</Badge> : null}
        </div>
        <div className="pack-approx">≈ {fmtNum(Math.round(tokens / 600))} ข้อความ*</div>
        <Button block size="lg" variant="primary" disabled={amount < g.minBaht || amount > g.maxBaht} onClick={() => onChoose({ kind: "tokens", amount: clamp(amount), tokens: paygTokens(pricing, clamp(amount)) })}>
          ชำระ {baht(clamp(amount))}
        </Button>
        {amount < g.minBaht ? <div className="tiny danger mt-4">ขั้นต่ำ {baht(g.minBaht)}</div> : null}
        <div className="tiny faint mt-8">* ประมาณจากข้อความละ ~600 token กับโมเดล ×1</div>
      </div>
    </div>
  );
}

export function PricingFaq() {
  const qa = [
    ["token คืออะไร?", "token คือหน่วยนับข้อความที่ AI อ่านและเขียน ภาษาไทยประมาณ 1 token ต่อ 1–2 ตัวอักษร ข้อความถาม-ตอบทั่วไปใช้ราว 300–800 token"],
    ["แพ็กเกจรายเดือนกับจ่ายตามการใช้งานต่างกันอย่างไร?", "แพ็กเกจให้โควตา token ทุกเดือน (รีเซ็ตทุกต้นเดือน) เหมาะกับคนใช้ประจำ ส่วนจ่ายตามการใช้งานคือซื้อ token เติมเข้ากระเป๋า ไม่มีวันหมดอายุ ใช้เท่าไรจ่ายเท่านั้น"],
    ["ถ้าโควตาเดือนนี้หมดแล้วจะเป็นอย่างไร?", "ระบบจะหัก token จากกระเป๋า (ที่ซื้อแบบจ่ายตามการใช้งาน) ต่ออัตโนมัติ ถ้าไม่มีเลยจะขึ้นให้อัปเกรดหรือซื้อเพิ่ม"],
    ["ทำไมแต่ละโมเดลหัก token ไม่เท่ากัน?", "โมเดลใหญ่มีต้นทุนสูงกว่า จึงมีตัวคูณ (เช่น ×2, ×12) โมเดลที่รันในเครื่องคุณคิดถูกที่สุด"],
    ["ชำระเงินอย่างไร?", "ชำระด้วย QR พร้อมเพย์ ผ่านแอปธนาคารได้ทุกธนาคาร ระบบเปิดใช้งานอัตโนมัติภายในไม่กี่วินาทีหลังจ่าย"],
  ];
  const [open, setOpen] = useState(0);
  return (
    <div className="faq">
      {qa.map(([q, a], i) => (
        <div key={q} className={"faq-item" + (open === i ? " is-open" : "")}>
          <button onClick={() => setOpen(open === i ? -1 : i)}>
            <span>{q}</span>
            <Icon name="down" size={18} />
          </button>
          {open === i ? <p>{a}</p> : null}
        </div>
      ))}
    </div>
  );
}
